/*
 * Healix Sets Collection — client controller for the "Sets" collection page.
 *
 * The server (blocks/collection-product-grid.liquid, sets branch) renders one
 * .cfs-section per ghost set product, each carrying:
 *   data-set-handle       — the ghost set's handle (deep-link target)
 *   data-set-top-handle   — the TOP component colourway anchor handle
 *   data-set-pant-handle  — the BOTTOM (pant) component colourway anchor handle
 *   data-set-url          — the set PDP url
 * with a skeleton row of .cfs-set-skeleton placeholders.
 *
 * For each set we load BOTH anchors' family siblings (HealixColors.siblingsRaw),
 * intersect them by colour slug — the colours available in BOTH the top and the
 * bottom family — using the SAME rules as the set PDP (assets/healix-set.js
 * buildShared/indexSiblings: both-limited => limited else core, ordered by the
 * backend order of the TOP family). Then per shared colour we render ONE set
 * card into the row and a swatch into the header swatch strip.
 *
 * CARD PARITY — CLONE THE REAL PRODUCT CARD (not hand-built markup)
 * ----------------------------------------------------------------
 * The Liquid block renders ONE real {% render 'product-card', product: cfs_set,
 * show_quick_add:false %} per section into an inert <template
 * data-set-card-template>. makeSetCard() deep-clones that content per shared
 * colour and PATCHES only the per-colour bits, so cards inherit the real
 * snippet's root CSS custom props, settings-driven aspect-ratio/rounding/colours
 * and inner structure — pixel parity with normal collection cards, no drift.
 *
 * Per-clone patch list:
 *   IMAGE  — the cloned media is REPLACED with a fresh <img> (a template-cloned
 *            img has a stale lazy-load state machine — see the note in
 *            makeSetCard); its src ← BOTTOM component's imageUrl for the colour
 *            (fallback top), loaded EAGERLY (no `loading` attr — see makeSetCard
 *            for why lazy was abandoned); every other media node + slider +
 *            <source> removed so no anchor srcset/imagery leaks; alt ←
 *            "<set title> — <colour>".
 *   TITLE  — set title (colour-agnostic).
 *   META   — colour name inserted into .product-card__meta (the slot normal
 *            cards use for the option subtitle).
 *   PRICE  — top + bottom summed for that colour, normalized defensively
 *            (siblings `price` is a DOLLAR STRING; theme formatMoney wants CENTS),
 *            patched INTO the real .product-card__price element. If either
 *            component's price is missing/unparseable the price wrapper is
 *            REMOVED (never show a wrong number).
 *   LIMITED — a "Limited" pc-limited-overlay chip when the shared colour is
 *            limited in both families (same mechanism the family JS uses).
 *   LINK   — every anchor's href → /products/<set-handle>?color=<slug>.
 *   STRIP  — nodes with no per-render disable param (rating, server badges,
 *            swatch/variant mounts, wishlist, quick-view, From/compare price,
 *            actions) are removed from the clone. show_quick_add:false in Liquid
 *            already suppresses the add-to-cart button before it renders.
 *
 * ISOLATION / REUSE-vs-REPLICATE DECISION
 * ---------------------------------------
 * collection-family-sections.js is intentionally NOT loaded on this page, and
 * our root does NOT carry data-family-sections. That script's init() claims the
 * single [data-family-sections] root and runs hydrateFamilies(), which would
 * fire HealixColors.families('sets','women') — a category the colours backend
 * does not know. That call IS graceful (families() catches the fetch throw and
 * resolves []), so no JS console error, but it still issues a doomed network
 * request every page load AND its renderSwatchRows()/hydrateCardTags() would
 * write into the very same [data-family-swatches] host and card badge slots we
 * populate, racing our DOM. Rather than depend on that ordering we keep full
 * isolation: we replicate only the small amount of chrome we actually need —
 * the horizontal scrubber under each row (mirrors initScrubber) and the fade
 * edge. We do NOT need lazy hydration (every card is client-rendered, no
 * <template> placeholders) nor the grid/scroll mode toggle (the toolbar toggle
 * targets [data-cfs-mode-toggle]; grid mode is a nice-to-have we skip to keep
 * this surface simple and conflict-free — rows scroll horizontally like the
 * default family view).
 *
 * ES5-leaning, no build step, depends only on HealixColors + window.formatMoney.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Money                                                              */
  /* ------------------------------------------------------------------ */

  // Mirror department-section.js normalizeMoneyInput: string -> dollars -> cents;
  // integer number >= 1000 -> already cents; other numbers -> dollars. Returns
  // null on empty/unparseable so callers can OMIT the price rather than show 0.
  function normalizeToCents(rawValue) {
    if (rawValue === null || rawValue === undefined || rawValue === '') return null;
    if (typeof rawValue === 'string') {
      var sanitized = rawValue.replace(/,/g, '').trim();
      if (sanitized === '') return null;
      var majorFromString = Number(sanitized);
      if (!isFinite(majorFromString)) return null;
      return Math.round(majorFromString * 100);
    }
    var numeric = Number(rawValue);
    if (!isFinite(numeric)) return null;
    if (isInteger(numeric) && Math.abs(numeric) >= 1000) return Math.round(numeric);
    return Math.round(numeric * 100);
  }

  function isInteger(n) {
    return typeof n === 'number' && isFinite(n) && Math.floor(n) === n;
  }

  function money(cents) {
    if (typeof window.formatMoney === 'function') {
      try { return window.formatMoney(cents, window.themeMoneyFormat); } catch (e) { /* fall through */ }
    }
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.Shopify.money_format) {
      try { return window.Shopify.formatMoney(cents, window.Shopify.money_format); } catch (e) { /* fall through */ }
    }
    if (typeof window.formatMoneyFallback === 'function') {
      try { return window.formatMoneyFallback(cents); } catch (e) { /* fall through */ }
    }
    return '$' + ((Number(cents) || 0) / 100).toFixed(2);
  }

  /* ------------------------------------------------------------------ */
  /* DOM helpers                                                        */
  /* ------------------------------------------------------------------ */

  function el(tag, cls, attrs) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (attrs) Object.keys(attrs).forEach(function (k) { e.setAttribute(k, attrs[k]); });
    return e;
  }

  // Request a CDN-sized render of a Shopify image url (same trick as healix-set.js).
  function sizedImage(url, size) {
    if (!url) return '';
    return String(url).replace(/(\.(?:jpg|jpeg|png|gif|webp))(\?.*)?$/i, '_' + size + '$1$2');
  }

  /* ------------------------------------------------------------------ */
  /* Colour chip — VERBATIM copy of collection-family-sections.js so the */
  /* set cards' colour tag is pixel-identical to the family cards'.      */
  /* (That file is intentionally not loaded here — see ISOLATION note.)  */
  /* ------------------------------------------------------------------ */

  // Perceived luminance (WCAG-ish) — decide white vs dark tag text on the hex.
  function tagTextColor(hex) {
    var h = String(hex || '').replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    if (h.length !== 6) return '#FFFFFF';
    var r = parseInt(h.slice(0, 2), 16) / 255;
    var g = parseInt(h.slice(2, 4), 16) / 255;
    var b = parseInt(h.slice(4, 6), 16) / 255;
    var lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return lum > 0.6 ? '#1D2129' : '#FFFFFF';
  }

  // Build one chip using the theme tag component's classes (snippets/tag.liquid):
  // .tag.tag--mini.tag--solid + .product-card__badge (badge-list sizing hook).
  function makeTag(text, hex, slug) {
    var tag = document.createElement('span');
    tag.className = 'tag tag--mini tag--solid product-card__badge' + (hex ? ' tag--hex' : ' tag--default');
    if (hex) {
      tag.style.setProperty('--tag-hex', hex);
      tag.style.setProperty('--tag-hex-text', tagTextColor(hex));
    }
    if (slug) tag.setAttribute('data-color-slug', slug);
    var txt = document.createElement('span');
    txt.className = 'tag__text';
    txt.textContent = text;
    tag.appendChild(txt);
    return tag;
  }

  // Mount point for our tags: the SAME containers badge-list.liquid renders
  // into, but always a dedicated (non-rotating) .product-card__badges div so
  // we never fight the badge-rotation script. Standard cards: inside
  // .product-card__info-row1 (created when the card had no badges/rating);
  // other card styles: an image-overlay container on the card root.
  function tagContainer(card) {
    var existing = card.querySelector('.product-card__badges.pc-color-tags');
    if (existing) return existing;

    var box = document.createElement('div');
    box.className = 'product-card__badges pc-color-tags';

    var info = card.querySelector('.product-card__info--below');
    if (info) {
      var row1 = info.querySelector('.product-card__info-row1');
      if (!row1) {
        row1 = document.createElement('div');
        row1.className = 'product-card__info-row1';
        info.insertBefore(row1, info.firstChild);
      }
      var spacer = row1.querySelector('.product-card__info-row1-spacer');
      var badgesRow = row1.querySelector('.product-card__badges-row');
      if (!badgesRow) {
        badgesRow = document.createElement('div');
        badgesRow.className = 'product-card__badges-row';
        if (spacer) row1.replaceChild(badgesRow, spacer);
        else row1.insertBefore(badgesRow, row1.firstChild);
      }
      badgesRow.appendChild(box);
    } else {
      // Non-standard card styles: overlay on the image (the image container
      // is the card's positioning context).
      var media = card.querySelector('.product-card__image-container') || card;
      media.appendChild(box);
    }
    return box;
  }

  /* ------------------------------------------------------------------ */
  /* Shared-colour intersection (mirrors healix-set.js)                 */
  /* ------------------------------------------------------------------ */

  function indexSiblings(sib) {
    // slug -> { name, hex, edition, handle, imageUrl, price, order }
    var map = {};
    var order = 0;
    if (!sib) return map;
    ['core', 'limited'].forEach(function (group) {
      (sib[group] || []).forEach(function (c) {
        var slug = String(c.color || '').toLowerCase();
        if (!slug || map[slug]) return;
        map[slug] = {
          name: c.colorName || c.color || 'Colour',
          hex: c.hex || '',
          edition: group,
          handle: c.handle,
          imageUrl: c.imageUrl || '',
          price: c.price,
          order: order++
        };
      });
    });
    return map;
  }

  function buildShared(topSib, pantSib) {
    var t = indexSiblings(topSib);
    var p = indexSiblings(pantSib);
    var out = [];
    Object.keys(t).forEach(function (slug) {
      if (!p[slug]) return;
      var te = t[slug], pe = p[slug];
      out.push({
        slug: slug,
        name: te.name,
        hex: te.hex || pe.hex,
        edition: (te.edition === 'limited' && pe.edition === 'limited') ? 'limited' : 'core',
        order: te.order,
        top: { handle: te.handle, imageUrl: te.imageUrl, price: te.price },
        pant: { handle: pe.handle, imageUrl: pe.imageUrl, price: pe.price }
      });
    });
    out.sort(function (a, b) { return a.order - b.order; });
    return out;
  }

  // Sum top + pant into cents; null if either side is missing/unparseable.
  function setPriceCents(color) {
    var topC = normalizeToCents(color.top.price);
    var pantC = normalizeToCents(color.pant.price);
    if (topC === null || pantC === null) return null;
    return topC + pantC;
  }

  /* ------------------------------------------------------------------ */
  /* Rendering                                                          */
  /* ------------------------------------------------------------------ */

  // Header swatch strip — same .cfs-swatch markup/classes the family JS emits,
  // grouped Core / Limited. Clicking a swatch flashes that colour's card in the
  // row (locateCard pattern; all cards exist post-hydration).
  function renderSwatchStrip(section, shared) {
    var host = section.querySelector('[data-family-swatches]');
    if (!host) return;
    host.textContent = '';
    host.setAttribute('role', 'group');
    host.setAttribute('aria-label', (section.getAttribute('data-family-title') || 'Set') + ' colours');

    var groups = [
      { label: 'Core', items: [] },
      { label: 'Limited', items: [] }
    ];
    shared.forEach(function (c) {
      (c.edition === 'limited' ? groups[1] : groups[0]).items.push(c);
    });

    var any = false;
    groups.forEach(function (g) {
      if (!g.items.length) return;
      any = true;
      var groupEl = el('div', 'cfs-swatches__group');
      var label = el('span', 'cfs-swatches__label');
      label.textContent = g.label;
      groupEl.appendChild(label);
      var dots = el('div', 'cfs-swatches__dots');
      g.items.forEach(function (c) { dots.appendChild(makeSwatch(section, c, g.label)); });
      groupEl.appendChild(dots);
      host.appendChild(groupEl);
    });
    host.hidden = !any;
  }

  function makeSwatch(section, color, editionLabel) {
    var btn = el('button', 'cfs-swatch', {
      type: 'button',
      title: color.name,
      'aria-label': color.name + ' — ' + editionLabel
    });
    if (color.slug) btn.setAttribute('data-color-slug', color.slug);
    var chip = el('span', 'cfs-swatch__chip');
    if (color.hex) chip.style.backgroundColor = color.hex;
    btn.appendChild(chip);
    btn.addEventListener('click', function () {
      section.querySelectorAll('.cfs-swatch.is-active').forEach(function (b) {
        if (b !== btn) b.classList.remove('is-active');
      });
      btn.classList.add('is-active');
      locateCard(section, color.slug);
    });
    return btn;
  }

  function findCard(section, slug) {
    if (!slug) return null;
    return section.querySelector('[data-product-card][data-color-slug="' + slug + '"]');
  }

  function flashCard(card) {
    var scope = card.closest('[data-sets-collection]') || document;
    scope.querySelectorAll('.cfs-card-flash').forEach(function (e) { e.classList.remove('cfs-card-flash'); });
    void card.offsetWidth; // restart animation if re-clicked
    card.classList.add('cfs-card-flash');
    card.addEventListener('animationend', function onEnd() {
      card.classList.remove('cfs-card-flash');
      card.removeEventListener('animationend', onEnd);
    });
  }

  function locateCard(section, slug) {
    var card = findCard(section, slug);
    if (!card) return;
    var row = section.querySelector('[data-set-row]');
    if (row) {
      var delta = card.getBoundingClientRect().left - row.getBoundingClientRect().left;
      row.scrollBy({ left: delta - 12, behavior: 'smooth' });
    }
    flashCard(card);
  }

  // One set card per shared colour, built by DEEP-CLONING the real product-card
  // markup (server-rendered once per section into <template data-set-card-template>)
  // and PATCHING it for this colour. Cloning the real snippet output is what
  // gives us pixel parity with normal collection cards: same root CSS custom
  // props, same settings-driven aspect-ratio/rounding/colours, same inner
  // structure. We only mutate the per-colour bits and strip the nodes that
  // don't belong on a set card.
  function makeSetCard(section, template, color, setHandle, setTitle) {
    var url = '/products/' + setHandle + '?color=' + encodeURIComponent(color.slug);

    var frag = template.content.cloneNode(true);
    var card = frag.querySelector('[data-product-card]') || frag.firstElementChild;
    if (!card) return null;

    // --- Identity / linking -------------------------------------------------
    // The card root is a <div>; every link inside (image container + any others)
    // must point at the set PDP for this colour. Mark the card for our locate/
    // swatch machinery and stamp the colour.
    card.setAttribute('data-color-slug', color.slug);
    card.setAttribute('data-set-card', '');
    card.classList.add('product-card--set-card');
    // The clone is now the BOTTOM anchor product's card, so every product-
    // specific identity attribute on the root points at the anchor, not the set.
    // Repoint the URL at the set PDP for this colour and STRIP the anchor's
    // product id/handle so nothing keys off the bottom product (wishlist,
    // quick-view, analytics). data-product-handle is also what the family JS's
    // hydrateCardTags keys on — removing it keeps that logic inert here.
    card.setAttribute('data-product-url', url);
    card.removeAttribute('data-product-id');
    card.removeAttribute('data-product-handle');
    card.removeAttribute('data-quick-view-card');
    var links = card.querySelectorAll('a[href]');
    for (var li = 0; li < links.length; li++) links[li].setAttribute('href', url);

    // --- Image --------------------------------------------------------------
    // The clone carries the BOTTOM anchor product's media. That product can have
    // MULTIPLE media (carousel slider with several <img>, a primary <video>, an
    // external-video poster) — all of it is the anchor's own imagery and must NOT
    // leak onto the set card. Reduce to a SINGLE primary <img> we control:
    //   1. Drop the carousel progress indicator (multi-media chrome).
    //   2. Find the first cloned <img> (styling/class reference only — see below).
    //   3. Remove EVERY cloned media node and the slider wrapper.
    //   4. Insert a FRESH <img> with our src; no srcset/data-srcset/<source>
    //      survives — the anchor's own srcset would otherwise override src with
    //      the wrong (bottom-product) imagery at some viewport widths.
    //
    // WHY A FRESH ELEMENT (do not "simplify" back to patching the clone):
    // Chromium quirk — an <img loading="lazy"> cloned from <template> content
    // has its lazy-load state machine initialized in the inert template
    // document. Patching src on that clone while detached leaves it PERMANENTLY
    // stalled: complete=false / naturalWidth=0 forever, even in-viewport
    // (verified live on this page; a fresh Image() with the identical src loads
    // instantly, and re-assigning img.src on the stuck node unsticks it). A
    // freshly created element in the live document has no such stale state, so
    // we always swap in a new <img> instead of patching the cloned one.
    var mediaBox = card.querySelector('.product-card__image-container');
    var imgUrl = color.pant.imageUrl || color.top.imageUrl || '';
    if (mediaBox) {
      // Carousel chrome / progress pill — never belongs on a single-image card.
      removeAll(mediaBox, ['.product-card__image-progress', '.product-card__image-slider .product-card__progress-pill']);

      // First cloned <img>, if any — used only to copy className/decoding so the
      // fresh element keeps the snippet's styling hooks.
      var mediaNodes = mediaBox.querySelectorAll('.product-card__media');
      var clonedImg = null;
      for (var mi = 0; mi < mediaNodes.length; mi++) {
        if (!clonedImg && mediaNodes[mi].tagName === 'IMG') clonedImg = mediaNodes[mi];
      }

      // Fresh, live-document <img> (see quirk note above).
      var primaryImg = document.createElement('img');
      primaryImg.className = clonedImg
        ? clonedImg.className
        : 'product-card__media product-card__image is-active';
      primaryImg.classList.remove('product-card__video', 'product-card__image--placeholder');
      if (!primaryImg.classList.contains('is-active')) primaryImg.classList.add('is-active');
      if (clonedImg && clonedImg.getAttribute('decoding')) {
        primaryImg.setAttribute('decoding', clonedImg.getAttribute('decoding'));
      }
      // Load EAGERLY: set the real src directly, NO `loading` attribute (so it
      // defaults to eager). Both native loading="lazy" AND a custom
      // IntersectionObserver failed to fire for these JS-inserted imgs in the
      // horizontal scroll-snap row (cards past the first few never loaded even
      // when scrolled into view). The card count here is small and bounded
      // (≤3 sets × ≤19 colours ≈ 57 images), comparable to a normal collection
      // page, so eager loading is fine and is the only provably-correct option.
      if (imgUrl) primaryImg.setAttribute('src', sizedImage(imgUrl, '600x'));
      primaryImg.setAttribute('alt', setTitle + ' — ' + color.name);

      // Where the cloned media sat: directly in the media box, or inside the
      // carousel slider wrapper. Insert the fresh img at that position, then
      // remove ALL cloned media nodes + the slider + any <source>.
      var slider = mediaBox.querySelector('.product-card__image-slider');
      var anchorNode = slider || (mediaNodes.length ? mediaNodes[0] : mediaBox.firstChild);
      if (anchorNode && anchorNode.parentNode === mediaBox) {
        mediaBox.insertBefore(primaryImg, anchorNode);
      } else {
        mediaBox.insertBefore(primaryImg, mediaBox.firstChild);
      }
      for (var mj = 0; mj < mediaNodes.length; mj++) {
        if (mediaNodes[mj].parentNode) mediaNodes[mj].parentNode.removeChild(mediaNodes[mj]);
      }
      removeAll(mediaBox, ['.product-card__image-slider', 'source']);
    }

    // --- Title --------------------------------------------------------------
    // The set title is colour-agnostic, so no per-colour patch is needed; but
    // normalise it to the set title (the clone already carries it verbatim).
    var titleText = card.querySelector('.product-card__title-text');
    if (titleText) titleText.textContent = setTitle;

    // --- Strip nodes that don't belong on a set card ------------------------
    // These are the anchor product's own settings-driven bits, no per-render
    // override in the snippet (only show_quick_add is a param — passed false in
    // Liquid). We KEEP .product-card__badges-row as a container (BUG 2: we inject
    // the hex colour chip into it, mirroring the family cards) and strip only its
    // PRE-EXISTING children — the anchor's server-rendered badges — below.
    //   • rating row (anchor's rating)
    //   • server badge chips inside the badges row (anchor tag badges)
    //   • any colour-swatch / variant mount + variant/meta JSON (anchor's own)
    //   • wishlist heart/button (carries the ANCHOR product's id/handle/price)
    //   • quick-view pill, "From" price, compare-at price, actions
    removeAll(card, [
      '.product-rating',
      '.product-card__badges',
      '.product-card__badge',
      '.product-card__swatches',
      '.product-card-swatches',
      '.product-card__color-select',
      '.product-card__variants',
      '.product-card__product-meta-json',
      '.product-card__variants-json',
      '.product-card__wishlist-heart',
      '.product-card__wishlist-btn',
      '.product-card__quickview',
      '.product-card__from',
      '.product-card__compare-price',
      '.product-card__actions'
    ]);
    // Empty the badges row of any anchor-product children (badge-list output),
    // but keep the row container itself so our colour chip lands where the
    // family cards' chip lands. Standard-card badges live in .product-card__info-
    // row1 alongside the rating; if that row now holds only our (soon-to-be-added)
    // chip container it stays, otherwise it collapses harmlessly.
    var badgesRow = card.querySelector('.product-card__badges-row');
    if (badgesRow) { while (badgesRow.firstChild) badgesRow.removeChild(badgesRow.firstChild); }

    // --- Colour chip (BUG 2) — replicate collection-family-sections.js --------
    // Normal family cards do NOT get a plain-text colour meta; they get a hex-
    // backed colour chip injected into .product-card__badges-row by that file's
    // makeTag (with luminance-based text colour) via tagContainer. Mirror that
    // exactly: same markup/classes/inline-style, same mount point.
    var chipBox = tagContainer(card);
    chipBox.appendChild(makeTag(color.name, color.hex || '', color.slug || ''));

    // --- Price: summed, normalized; OMIT the price element if unparseable ---
    // Patch INTO the card's real price element (.product-card__price inside
    // [data-price-wrapper]); leave its classes/CSS vars intact for parity.
    var cents = setPriceCents(color);
    var priceEl = card.querySelector('[data-price-wrapper] .product-card__price');
    if (cents !== null) {
      if (priceEl) {
        priceEl.classList.remove('product-card__price--sale');
        priceEl.textContent = money(cents);
      }
    } else {
      // Unparseable → never show a wrong number: drop the whole price wrapper.
      removeAll(card, ['.product-card__row3', '[data-price-wrapper]']);
    }

    // --- Limited treatment — mirror family JS hydrateCardTags exactly --------
    // For a limited-edition colour the family JS builds makeTag('Limited','','')
    // (→ .tag.tag--mini.tag--solid.product-card__badge.tag--default), adds
    // .pc-limited-overlay, and appends it to the IMAGE container as a top-left
    // overlay (not into the badges row). Replicate byte-for-byte.
    if (color.edition === 'limited') {
      var limited = makeTag('Limited', '', '');
      limited.classList.add('pc-limited-overlay');
      var limitedMedia = card.querySelector('.product-card__image-container');
      (limitedMedia || chipBox).appendChild(limited);
    }

    return card;
  }

  // Remove every node matching any selector in `sels` from `root`.
  function removeAll(root, sels) {
    for (var s = 0; s < sels.length; s++) {
      var nodes = root.querySelectorAll(sels[s]);
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].parentNode) nodes[i].parentNode.removeChild(nodes[i]);
      }
    }
  }

  function clearSkeletons(row) {
    var sk = row.querySelectorAll('[data-set-skeleton]');
    for (var i = 0; i < sk.length; i++) sk[i].parentNode.removeChild(sk[i]);
  }

  function renderEmpty(section, row) {
    clearSkeletons(row);
    var msg = el('p', 'cfs-set-empty');
    var setUrl = section.getAttribute('data-set-url') || '';
    msg.innerHTML = 'No matching colours right now. ' +
      (setUrl ? '<a href="' + setUrl + '">View the set</a>.' : '');
    row.appendChild(msg);
  }

  /* ------------------------------------------------------------------ */
  /* Scrubber (replicated from collection-family-sections.js initScrubber) */
  /* ------------------------------------------------------------------ */

  function initScrubber(section) {
    var row = section.querySelector('[data-set-row]');
    var wrap = section.querySelector('.cfs-row-wrap');
    if (!row || !wrap) return;
    if (section._setScrubReady) return;
    section._setScrubReady = true;

    var scrub = el('div', 'cfs-scrub');
    var track = el('div', 'cfs-scrub__track');
    var handle = el('div', 'cfs-scrub__handle');
    handle.tabIndex = 0;
    handle.setAttribute('role', 'scrollbar');
    handle.setAttribute('aria-orientation', 'horizontal');
    handle.setAttribute('aria-label', 'Scroll ' + (section.getAttribute('data-family-title') || 'set colours'));
    handle.setAttribute('aria-valuemin', '0');
    handle.setAttribute('aria-valuemax', '100');
    track.appendChild(handle);
    scrub.appendChild(track);
    wrap.insertAdjacentElement('afterend', scrub);

    function maxScroll() { return row.scrollWidth - row.clientWidth; }

    function sync() {
      var max = maxScroll();
      var atEnd = row.scrollLeft >= max - 2;
      wrap.toggleAttribute('data-at-end', atEnd);
      if (max <= 2) { scrub.hidden = true; return; }
      scrub.hidden = false;
      var x = (track.clientWidth - handle.offsetWidth) * (row.scrollLeft / max);
      handle.style.transform = 'translateX(' + Math.max(0, x) + 'px)';
      handle.setAttribute('aria-valuenow', String(Math.round((row.scrollLeft / max) * 100)));
    }

    var drag = null;
    handle.addEventListener('pointerdown', function (e) {
      drag = { x: e.clientX, left: row.scrollLeft };
      handle.setPointerCapture(e.pointerId);
      scrub.classList.add('is-dragging');
      row.classList.add('cfs-row--free');
      e.preventDefault();
    });
    handle.addEventListener('pointermove', function (e) {
      if (!drag) return;
      var range = track.clientWidth - handle.offsetWidth;
      var max = maxScroll();
      if (range <= 0 || max <= 0) return;
      row.scrollLeft = drag.left + (e.clientX - drag.x) * (max / range);
    });
    function endDrag() {
      drag = null;
      scrub.classList.remove('is-dragging');
      row.classList.remove('cfs-row--free');
    }
    handle.addEventListener('pointerup', endDrag);
    handle.addEventListener('pointercancel', endDrag);

    scrub.addEventListener('pointerdown', function (e) {
      if (e.target === handle || drag) return;
      var rect = track.getBoundingClientRect();
      var range = rect.width - handle.offsetWidth;
      if (range <= 0) return;
      var ratio = (e.clientX - rect.left - handle.offsetWidth / 2) / range;
      ratio = Math.max(0, Math.min(1, ratio));
      row.scrollTo({ left: ratio * maxScroll(), behavior: 'smooth' });
    });

    handle.addEventListener('keydown', function (e) {
      var step = row.clientWidth * 0.5;
      if (e.key === 'ArrowRight') { row.scrollBy({ left: step, behavior: 'smooth' }); e.preventDefault(); }
      else if (e.key === 'ArrowLeft') { row.scrollBy({ left: -step, behavior: 'smooth' }); e.preventDefault(); }
    });

    row.addEventListener('scroll', sync, { passive: true });
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(sync);
      ro.observe(row);
      ro.observe(track);
    } else {
      window.addEventListener('resize', sync);
    }
    sync();
  }

  /* ------------------------------------------------------------------ */
  /* Per-set hydration                                                  */
  /* ------------------------------------------------------------------ */

  function hydrateSet(section) {
    var row = section.querySelector('[data-set-row]');
    if (!row) return;
    var template = section.querySelector('template[data-set-card-template]');
    var topHandle = (section.getAttribute('data-set-top-handle') || '').trim();
    var pantHandle = (section.getAttribute('data-set-pant-handle') || '').trim();
    var setHandle = (section.getAttribute('data-set-handle') || '').trim();
    var setTitle = section.getAttribute('data-family-title') || '';
    if (!topHandle || !pantHandle || !setHandle || !template) { clearSkeletons(row); return; }

    Promise.all([
      window.HealixColors.siblingsRaw(topHandle),
      window.HealixColors.siblingsRaw(pantHandle)
    ]).then(function (res) {
      var shared = buildShared(res[0], res[1]);
      if (!shared.length) { renderEmpty(section, row); return; }

      clearSkeletons(row);
      shared.forEach(function (color) {
        var card = makeSetCard(section, template, color, setHandle, setTitle);
        if (card) row.appendChild(card);
      });
      section.setAttribute('data-family-count', String(shared.length));

      renderSwatchStrip(section, shared);
      initScrubber(section);
    }).catch(function () {
      // Total failure — leave the set discoverable via its PDP.
      renderEmpty(section, row);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Init                                                               */
  /* ------------------------------------------------------------------ */

  function init() {
    var root = document.querySelector('[data-sets-collection]');
    if (!root) return;
    if (!window.HealixColors || !window.HealixColors.siblingsRaw) return;

    var sections = Array.prototype.slice.call(root.querySelectorAll('[data-set-family]'));

    function start() {
      sections.forEach(function (section) { hydrateSet(section); });
    }
    // Start on idle after load so hydration doesn't compete with first paint.
    if ('requestIdleCallback' in window) {
      requestIdleCallback(start, { timeout: 2000 });
    } else {
      setTimeout(start, 200);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
